//can be moved to global molecule components
import { useTheme } from '@mui/material';
import { addHours } from 'date-fns';
import { useTranslation } from 'react-i18next';
import DatePicker, {
  DateObject,
  DatePickerProps,
  HeaderItem,
} from 'react-multi-date-picker';
import { darkNeutral, lightNeutral } from 'src/colors';
import { InputField } from 'src/components/atoms/input-field';
import { dateFormats } from 'src/utils/date-formats';

import {
  MonthsTranslationKeys,
  WeekDaysTranslationKeys,
} from './constants/dates';
import './date-range-select.styles.css';
import CustomToolbar from './custom-toolbar';
import { useRef } from 'react';
import { CustomHeader } from './custom-header';

interface Props extends DatePickerProps<false, true> {
  numberOfMonths?: number;
  onlyMonthPicker?: boolean;
  onlyYearPicker?: boolean;
  minDate?: Date;
  maxDate?: Date;
  value: DateObject[];
  format?: string;
  onApply?: () => void;
  headerOrder?: HeaderItem[];
}

export const DATE_RANGE_DATE_FORMAT = `${
  dateFormats.month
}/${dateFormats.day.toUpperCase()}/${dateFormats?.year.toUpperCase()}`;

const DateRangeSelect = ({
  onlyMonthPicker,
  onlyYearPicker,
  numberOfMonths,
  minDate,
  maxDate,
  value,
  onChange,
  format,
  onApply,
  ...rest
}: Props) => {
  const pickerRef = useRef<
    typeof DatePicker & {
      closeCalendar: () => void;
    }
  >(null);

  const { t } = useTranslation();

  const {
    palette: { mode },
  } = useTheme();

  const onClose = () => {
    pickerRef?.current?.closeCalendar();
  };

  const onClear = () => {
    onChange?.([], {
      validatedValue: '',
      input: {} as HTMLElement,
      isTyping: false,
    });
  };

  return (
    <DatePicker
      {...rest}
      range
      rangeHover
      onlyMonthPicker={onlyMonthPicker}
      onlyYearPicker={onlyYearPicker}
      numberOfMonths={numberOfMonths}
      value={value}
      onChange={onChange}
      className={mode === 'dark' ? 'dark-custom-calendar' : 'custom-calendar'}
      dateSeparator=" - "
      format={format}
      ref={pickerRef}
      minDate={minDate}
      weekStartDayIndex={1}
      maxDate={maxDate || addHours(new Date(), 1)}
      months={Object.values(MonthsTranslationKeys).map(day =>
        t(`common:calendar.months.${day}`),
      )}
      weekDays={Object.values(WeekDaysTranslationKeys).map(day =>
        t(`common:calendar.weekDays.${day}`),
      )}
      mapDays={({ date, today, selectedDate, isSameDate }) => {
        const props = {
          style: {
            borderRadius: '50px',
            backgroundColor: 'transparent',
            border: 'none',
          },
        };

        if (
          (isSameDate(date, selectedDate?.[0]) &&
            !isSameDate(selectedDate?.[0], today)) ||
          (isSameDate(date, selectedDate?.[1]) &&
            !isSameDate(selectedDate?.[1], today))
        ) {
          props.style = {
            ...props.style,
            backgroundColor:
              mode === 'dark' ? darkNeutral[100] : lightNeutral[100],
            border: ' 1px solid #FF8C00',
            // @ts-expect-error TYPESCRIPT THROWING ERROR THAT COLOR IS NOT A VALID PROPERTY BUT WE CAN PASS IT BECAUSE IT IS A VALID PROPERTY
            color: mode === 'dark' ? '#fff' : '#000',
          };
        }

        return props;
      }}
      render={
        <InputField
          placeholder={t('common:calendar.selectDateRange')}
          sx={{ width: '200px' }}
          inputSx={{ py: 1.1 }}
        />
      }
      plugins={[
        <CustomToolbar
          position="bottom"
          key="custom-toolbar"
          onApply={onApply ? onApply : () => null}
          onClose={onClose}
          onClear={onClear}
        />,
        <CustomHeader
          position="top"
          value={value}
          onlyMonthPicker={onlyMonthPicker}
        />,
      ]}
    />
  );
};

export default DateRangeSelect;
